import {
	BackSide,
	BoxGeometry,
	Mesh,
	ShaderMaterial,
	UniformsUtils,
	Vector3
} from 'three';

/**
 * Enhanced Sky with realistic sunset/sunrise effects.
 * Features: Reddening/enlarging sun disc near horizon, improved atmospheric scattering,
 * soft sun edges, and optimized cloud rendering.
 * Self-hosted copy of three's Sky addon so we control the shader.
 */
class Sky extends Mesh {

	constructor() {

		const shader = Sky.SkyShader;

		const material = new ShaderMaterial( {
			name: shader.name,
			uniforms: UniformsUtils.clone( shader.uniforms ),
			vertexShader: shader.vertexShader,
			fragmentShader: shader.fragmentShader,
			side: BackSide,
			depthWrite: false
		} );

		super( new BoxGeometry( 1, 1, 1 ), material );

		this.isSky = true;

	}

}

Sky.SkyShader = {

	name: 'SkyShader',

	uniforms: {
		'turbidity': { value: 2 },
		'rayleigh': { value: 1 },
		'mieCoefficient': { value: 0.005 },
		'mieDirectionalG': { value: 0.8 },
		'sunPosition': { value: new Vector3() },
		'up': { value: new Vector3( 0, 1, 0 ) },
		'cloudScale': { value: 0.0002 },
		'cloudSpeed': { value: 0.00007 },
		'cloudCoverage': { value: 0.4 },
		'cloudDensity': { value: 0.4 },
		'cloudElevation': { value: 0.5 },
		'time': { value: 0.0 },
		'sunDiskScale': { value: 1.0 },      // Base sun size multiplier
		'sunsetReddening': { value: 1.0 },    // Intensity of red shift (0-1)
		'sunBrightness': { value: 1.0 },      // Sun-disc intensity multiplier (tame the blown-out disc)
		'cloudBright': { value: 1.0 },        // Cloud-color multiplier (<1 = darker/grayer clouds for overcast)
		'skyExposure': { value: 1.0 }         // Whole-dome brightness multiplier (<1 = darker sky, independent of renderer tone-map)
	},

	vertexShader: /* glsl */`
		uniform vec3 sunPosition;
		uniform float rayleigh;
		uniform float turbidity;
		uniform float mieCoefficient;
		uniform vec3 up;

		varying vec3 vWorldPosition;
		varying vec3 vSunDirection;
		varying vec3 vBetaR;
		varying vec3 vBetaM;
		varying float vSunE;
		varying float vSunElevation; // Pass sun elevation to fragment shader

		const float e = 2.71828182845904523536028747135266249775724709369995957;
		const float pi = 3.141592653589793238462643383279502884197169;

		const vec3 lambda = vec3( 680E-9, 550E-9, 450E-9 );
		const vec3 totalRayleigh = vec3( 5.804542996261093E-6, 1.3562911419845635E-5, 3.0265902468824876E-5 );

		const float v = 4.0;
		const vec3 K = vec3( 0.686, 0.678, 0.666 );
		const vec3 MieConst = vec3( 1.8399918514433978E14, 2.7798023919660528E14, 4.0790479543861094E14 );

		const float cutoffAngle = 1.6110731556870734;
		const float steepness = 1.5;
		const float EE = 1000.0;

		float sunIntensity( float zenithAngleCos ) {
			zenithAngleCos = clamp( zenithAngleCos, -1.0, 1.0 );
			return EE * max( 0.0, 1.0 - pow( e, -( ( cutoffAngle - acos( zenithAngleCos ) ) / steepness ) ) );
		}

		vec3 totalMie( float T ) {
			float c = ( 0.2 * T ) * 10E-18;
			return 0.434 * c * MieConst;
		}

		void main() {

			vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
			vWorldPosition = worldPosition.xyz;

			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			gl_Position.z = gl_Position.w;

			vSunDirection = normalize( sunPosition );

			// Calculate sun elevation for sunset effects
			vSunElevation = dot( vSunDirection, up );

			vSunE = sunIntensity( vSunElevation );

			float vSunfade = 1.0 - clamp( 1.0 - exp( ( sunPosition.y / 450000.0 ) ), 0.0, 1.0 );

			float rayleighCoefficient = rayleigh - ( 1.0 * ( 1.0 - vSunfade ) );

			vBetaR = totalRayleigh * rayleighCoefficient;
			vBetaM = totalMie( turbidity ) * mieCoefficient;

		}`,

	fragmentShader: /* glsl */`
		varying vec3 vWorldPosition;
		varying vec3 vSunDirection;
		varying vec3 vBetaR;
		varying vec3 vBetaM;
		varying float vSunE;
		varying float vSunElevation;

		uniform float mieDirectionalG;
		uniform vec3 up;
		uniform float cloudScale;
		uniform float cloudSpeed;
		uniform float cloudCoverage;
		uniform float cloudDensity;
		uniform float cloudElevation;
		uniform float time;
		uniform float sunDiskScale;
		uniform float sunsetReddening;
		uniform float sunBrightness;
		uniform float cloudBright;
		uniform float skyExposure;

		// Optimized noise functions
		float hash( vec2 p ) {
			return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453123 );
		}

		float noise( vec2 p ) {
			vec2 i = floor( p );
			vec2 f = fract( p );
			f = f * f * ( 3.0 - 2.0 * f );
			float a = hash( i );
			float b = hash( i + vec2( 1.0, 0.0 ) );
			float c = hash( i + vec2( 0.0, 1.0 ) );
			float d = hash( i + vec2( 1.0, 1.0 ) );
			return mix( mix( a, b, f.x ), mix( c, d, f.x ), f.y );
		}

		float fbm( vec2 p ) {
			float value = 0.0;
			float amplitude = 0.5;
			for ( int i = 0; i < 4; i ++ ) { // Reduced from 5 to 4 for performance
				value += amplitude * noise( p );
				p *= 2.0;
				amplitude *= 0.5;
			}
			return value;
		}

		const float pi = 3.141592653589793238462643383279502884197169;
		const float n = 1.0003;
		const float N = 2.545E25;

		const float rayleighZenithLength = 8.4E3;
		const float mieZenithLength = 1.25E3;
		
		// Base sun angular diameter (approx 0.5 degrees)
		const float sunAngularDiameterCos = 0.999956676946448443553574619906976478926848692873900859324;

		const float THREE_OVER_SIXTEENPI = 0.05968310365946075;
		const float ONE_OVER_FOURPI = 0.07957747154594767;

		float rayleighPhase( float cosTheta ) {
			return THREE_OVER_SIXTEENPI * ( 1.0 + pow( cosTheta, 2.0 ) );
		}

		float hgPhase( float cosTheta, float g ) {
			float g2 = pow( g, 2.0 );
			float inverse = 1.0 / pow( 1.0 - 2.0 * g * cosTheta + g2, 1.5 );
			return ONE_OVER_FOURPI * ( ( 1.0 - g2 ) * inverse );
		}

		void main() {

			vec3 direction = normalize( vWorldPosition - cameraPosition );

			// Optical length calculation
			float zenithAngle = acos( max( 0.0, dot( up, direction ) ) );
			float inverse = 1.0 / ( cos( zenithAngle ) + 0.15 * pow( 93.885 - ( ( zenithAngle * 180.0 ) / pi ), -1.253 ) );
			float sR = rayleighZenithLength * inverse;
			float sM = mieZenithLength * inverse;

			// Combined extinction factor
			vec3 Fex = exp( -( vBetaR * sR + vBetaM * sM ) );

			// In scattering
			float cosTheta = dot( direction, vSunDirection );

			float rPhase = rayleighPhase( cosTheta * 0.5 + 0.5 );
			vec3 betaRTheta = vBetaR * rPhase;

			float mPhase = hgPhase( cosTheta, mieDirectionalG );
			vec3 betaMTheta = vBetaM * mPhase;

			vec3 Lin = pow( vSunE * ( ( betaRTheta + betaMTheta ) / ( vBetaR + vBetaM ) ) * ( 1.0 - Fex ), vec3( 1.5 ) );
			Lin *= mix( vec3( 1.0 ), pow( vSunE * ( ( betaRTheta + betaMTheta ) / ( vBetaR + vBetaM ) ) * Fex, vec3( 1.0 / 2.0 ) ), clamp( pow( 1.0 - dot( up, vSunDirection ), 5.0 ), 0.0, 1.0 ) );

			// Night sky base
			vec3 L0 = vec3( 0.1 ) * Fex;

			// ============================================
			// ENHANCED SUN DISC WITH SUNSET EFFECTS
			// ============================================
			
			// Calculate sunset factor (0 = noon/high sun, 1 = horizon)
			// vSunElevation ranges from -1 (below horizon) to 1 (zenith)
			float sunsetFactor = clamp( 1.0 - ( vSunElevation + 0.2 ) * 1.25, 0.0, 1.0 );
			sunsetFactor = pow( sunsetFactor, 2.0 ) * sunsetReddening; // Smooth curve
			
			// Dynamic sun size: grows up to 1.5x larger at horizon
			float dynamicSunScale = mix( 1.0, 1.5, sunsetFactor ) * sunDiskScale;
			
			// Adjusted angular diameter for sunset enlargement
			float adjustedSunCos = 1.0 - ( 1.0 - sunAngularDiameterCos ) / dynamicSunScale;
			
			// Softer edge falloff that also widens at sunset
			float edgeSoftness = 0.00002 + ( sunsetFactor * 0.00005 );
			float sundisk = smoothstep( adjustedSunCos, adjustedSunCos + edgeSoftness, cosTheta );
			
			// Sun color temperature shift: white-yellow-orange-red
			// At sunset: boost red, reduce green/blue
			vec3 sunColor = vec3( 1.0, 0.98, 0.95 ); // Base: slightly warm white
			sunColor = mix( sunColor, vec3( 1.0, 0.6, 0.2 ), sunsetFactor * 0.7 ); // Orange shift
			sunColor = mix( sunColor, vec3( 1.0, 0.2, 0.05 ), sunsetFactor * 0.5 ); // Deep red at extreme sunset
			
			// Apply sun disc with atmospheric extinction
			// Reduce intensity near horizon due to atmospheric extinction
			float horizonExtinction = mix( 1.0, 0.3, sunsetFactor );
			L0 += ( vSunE * 19000.0 * Fex * sunColor * horizonExtinction * sunBrightness ) * sundisk;

			vec3 texColor = ( Lin + L0 ) * 0.04 + vec3( 0.0, 0.0003, 0.00075 );

			// Dim the blue sky *before* the clouds blend in, so lowering skyExposure
			// darkens the dome but leaves the clouds bright — making them MORE visible
			// against an overcast sky, not less.
			texColor *= skyExposure;

			// ============================================
			// NIGHT HORIZON AIRGLOW
			// ============================================
			// Once the sun dips below the horizon the dome goes near-black; add a faint
			// cool glow that's strongest at the horizon and fades with elevation, so a
			// little light creeps up around the edge instead of pure black.
			float nightFactor = smoothstep( 0.05, -0.15, vSunElevation ); // 0 above horizon → 1 once sun is under
			float horizonGlow  = exp( -max( 0.0, direction.y ) * 4.0 );    // bright at horizon, fading upward
			vec3  glowColor    = vec3( 0.020, 0.030, 0.055 );              // faint cool blue
			texColor += glowColor * horizonGlow * nightFactor;

			// ============================================
			// CLOUDS WITH IMPROVED HORIZON HANDLING
			// ============================================
			
			if ( direction.y > 0.0 && cloudCoverage > 0.0 ) {

				// Project to cloud plane
				float elevation = mix( 1.0, 0.1, cloudElevation );
				vec2 cloudUV = direction.xz / max( direction.y * elevation, 0.001 ); // Prevent div by zero
				cloudUV *= cloudScale;
				cloudUV += time * cloudSpeed;

				// Multi-octave noise
				float cloudNoise = fbm( cloudUV * 1000.0 );
				cloudNoise += 0.5 * fbm( cloudUV * 2000.0 + 3.7 );
				cloudNoise = cloudNoise * 0.5 + 0.5;

				// Coverage threshold with softer transition
				float coverageEdge = 0.3 * ( 1.0 - cloudCoverage + 0.1 );
				float cloudMask = smoothstep( 1.0 - cloudCoverage, 1.0 - cloudCoverage + coverageEdge, cloudNoise );

				// Improved horizon fade - exponential for smoother transition
				float horizonFade = exp( -max( 0.0, -direction.y * 10.0 ) );
				horizonFade *= smoothstep( 0.0, 0.15, direction.y );
				cloudMask *= horizonFade;

				// Cloud lighting with sunset coloring
				float sunInfluence = dot( direction, vSunDirection ) * 0.5 + 0.5;
				float daylight = max( 0.0, vSunDirection.y * 2.0 );

				// Base cloud color affected by atmosphere and sunset
				vec3 atmosphereColor = Lin * 0.04;
				vec3 cloudColor = mix( vec3( 0.3 ), vec3( 1.0 ), daylight );
				
				// Sunset reddening for clouds facing the sun
				vec3 sunsetCloudColor = mix( cloudColor, vec3( 1.0, 0.5, 0.3 ), sunsetFactor * sunInfluence );
				cloudColor = mix( cloudColor, sunsetCloudColor, sunsetFactor );
				
				cloudColor = mix( cloudColor, atmosphereColor + vec3( 1.0 ), sunInfluence * 0.5 );
				cloudColor *= vSunE * 0.00002 * cloudBright;

				// Blend clouds with sky
				texColor = mix( texColor, cloudColor, cloudMask * cloudDensity );

			}

			gl_FragColor = vec4( texColor, 1.0 );

			#include <tonemapping_fragment>
			#include <colorspace_fragment>

		}`

};

export { Sky };